import { useEffect, useState, useRef } from 'react';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { useRouter, useSegments, useLocalSearchParams } from 'expo-router';
import { Dimensions } from 'react-native';
import { logger } from '@/lib/logger';
import { Href } from 'expo-router/build/typed-routes/types';

export const useLayoutSwitcher = () => {
  const router = useRouter();
  const segments = useSegments();
  const params = useLocalSearchParams();
  const [isMounted, setIsMounted] = useState(false);

  // Store latest values in refs to avoid re-running effect on every route change
  const segmentsRef = useRef(segments);
  const paramsRef = useRef(params);

  // Update refs whenever values change
  segmentsRef.current = segments;
  paramsRef.current = params;

  const buildTargetPathWithParams = (targetLayout: string) => {
    // Get segments after the layout (index 2+)
    const routeSegments = segmentsRef.current.slice(2);

    // Map segments to actual values using params
    const mappedSegments = routeSegments.map((segment) => {
      // If segment is a dynamic route like [id] or [...slug]
      if (segment.startsWith('[') && segment.endsWith(']')) {
        const paramName = segment.slice(1, -1); // Remove brackets

        // Handle catch-all routes [...slug]
        if (paramName.startsWith('...')) {
          const actualParamName = paramName.slice(3); // Remove ...
          const paramValue = paramsRef.current[actualParamName];
          // Catch-all params are arrays, join them
          if (Array.isArray(paramValue)) {
            return paramValue.join('/');
          }
          return paramValue || segment; // Fallback to original if no param
        }

        // Handle regular dynamic routes [id]
        return paramsRef.current[paramName] || segment; // Fallback to original if no param
      }

      // Regular segment, return as-is
      return segment;
    });

    const basePath = `/(private)/${targetLayout}`;

    if (mappedSegments.length > 0) {
      return `${basePath}/${mappedSegments.join('/')}`;
    }

    return basePath;
  };

  const switchToCorrectLayout = (width: number) => {
    if (!isMounted) return;

    const isMobile = width < LayoutBreakpoints.tablet;
    const currentLayout = segmentsRef.current[1];
    const targetLayout = isMobile ? '(tabs)' : '(sidebar)';

    // Don't switch if already on correct layout
    if (currentLayout === targetLayout) return;

    const targetPath = buildTargetPathWithParams(targetLayout);
    logger.debug('Layout switch required - navigating to:', targetPath);
    router.replace(targetPath as Href);
  };

  // Set mounted flag
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Handle initial screen size and changes
  useEffect(() => {
    if (!isMounted) return;

    // Handle initial screen size
    const { width } = Dimensions.get('window');
    switchToCorrectLayout(width);

    // Listen for screen size changes
    const unsubscribe = Dimensions.addEventListener('change', ({ window }) => {
      switchToCorrectLayout(window.width);
    });

    return () => {
      unsubscribe.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);
};
